import React, { useState } from 'react'

import { clientApi } from '@/api/client'

type ReportKind = 'company' | 'question' | 'source_set' | 'workspace'

type Props = {
  kind: ReportKind
  /** Numeric company_id, string item_id, or string set_id depending on ``kind``. */
  targetId: number | string
  /** Optional custom label; defaults to "Download PDF report". */
  label?: string
  /** Render as a small icon-style button rather than a full button. */
  compact?: boolean
  /** Hide the secondary "Preview" button. Useful in tight rows where the
   *  caller wires its own preview affordance. */
  hidePreview?: boolean
}

/** Source-set HTML previews exist for internal template iteration but are
 *  staff-gated on the API, so the customer dashboard only exposes previews
 *  for report kinds that share the same access guard as the PDF endpoint. */
function supportsCustomerPreview(kind: ReportKind): boolean {
  return kind === 'company' || kind === 'question' || kind === 'workspace'
}

/**
 * Triggers a Cenva Intelligence PDF download for a company, question,
 * source-set, or workspace. Shows inline progress state and surfaces server errors
 * so the user knows what happened (rather than silently failing).
 *
 * In addition to the PDF download, this component renders a secondary
 * "Preview" button that opens the report's HTML view in a new tab so the
 * user can review the document before committing to the PDF. The HTML is
 * byte-equivalent to the PDF input, so what they see is what they print.
 *
 * Kept as a leaf component so we can drop it into multiple routes
 * (CompanyDetail, ItemDetail, QuestionSetDetail) without prop-drilling
 * react-query state.
 */
export default function DownloadReportButton({
  kind,
  targetId,
  label,
  compact = false,
  hidePreview = false,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function onClick() {
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      if (kind === 'company') {
        await clientApi.downloadCompanyReport(Number(targetId))
      } else if (kind === 'question') {
        await clientApi.downloadQuestionReport(String(targetId))
      } else if (kind === 'workspace') {
        await clientApi.downloadWorkspaceReport(String(targetId))
      } else {
        await clientApi.downloadQuestionSetReport(String(targetId))
      }
    } catch (e: any) {
      setErr(e?.message || 'Download failed')
    } finally {
      setBusy(false)
    }
  }

  async function onPreviewClick() {
    if (previewBusy) return
    setPreviewBusy(true)
    setErr(null)
    try {
      if (kind === 'company') {
        await clientApi.previewCompanyReport(Number(targetId))
      } else if (kind === 'question') {
        await clientApi.previewQuestionReport(String(targetId))
      } else if (kind === 'workspace') {
        await clientApi.previewWorkspaceReport(String(targetId))
      }
    } catch (e: any) {
      setErr(e?.message || 'Preview failed')
    } finally {
      setPreviewBusy(false)
    }
  }

  const text = busy
    ? 'Preparing PDF…'
    : label || (compact ? 'PDF' : 'Download PDF report')

  const showPreview = !hidePreview && supportsCustomerPreview(kind)

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {showPreview ? (
        <button
          type="button"
          className="btn"
          onClick={onPreviewClick}
          disabled={previewBusy}
          title="Open the report's HTML preview in a new tab"
          aria-label={`Preview ${kind} report`}
          data-testid={`preview-${kind}-report`}
        >
          {previewBusy ? 'Opening…' : compact ? 'Preview' : 'Preview report'}
        </button>
      ) : null}
      <button
        type="button"
        className="btn"
        onClick={onClick}
        disabled={busy}
        title="Download a branded Cenva Intelligence PDF report"
        aria-label={`Download ${kind} report as PDF`}
        data-testid={`download-${kind}-report`}
      >
        {text}
      </button>
      {err && (
        <span role="alert" style={{ fontSize: 12, color: 'var(--bad, #ef4444)' }}>
          {err}
        </span>
      )}
    </span>
  )
}
